import { useRef } from 'react';
import { toPostUI } from '../../mappers/posts/toPostUI';
import { Box } from '../../ui/layout/Box';
import { DataTestId } from '../../ui/layout/DataTestIdWrapper';
import { ResizableGrid } from '../../ui/layout/ResizableGrid/ResizableGrid';
import { PostCardGrid } from '../../ui/posts/PostCardGrid/PostCardGrid';
import { calculateGridCardHeight } from '../../ui/posts/PostCardGrid/utils';
import { EventSource } from '../../utils/analyticsService';
import { useGridImageOptimizer } from '../../utils/mds/useGridImageAlias';
import { UsePostsCollectionPayload } from '../post/usePostCollection';
import { withPostCallbacks } from '../post/withPostCallbacks';
import { SearchNotFound } from './SearchNotFound';
import { useResetScroll } from './hooks/useResetScroll';

interface SearchPostProps {
    postCollection: UsePostsCollectionPayload;
}

const EnchancedPostGridCardCompact = withPostCallbacks(PostCardGrid, EventSource.SEARCH);

export const SearchPost = (props: SearchPostProps) => {
    const { postCollection } = props;
    const gridContainerRef = useRef<HTMLDivElement>(null);
    const hasPosts = postCollection.isLoading || postCollection.items.length > 0;
    const { getAlias: getGridImageAliasForCard } = useGridImageOptimizer();

    const { onScroll } = useResetScroll(gridContainerRef, 'searchPostsScrollTop', hasPosts);

    const getItemHeight = (index: number, width: number) => {
        const postModel = postCollection.items[index];
        if (!postModel) {
            return 0;
        }

        return calculateGridCardHeight(width, toPostUI(postModel).content, postModel.postType);
    };

    const renderItem = (
        index: number,
        columns: number,
        isVisible?: boolean,
        cardSize?: { width: number; height: number },
    ) => {
        const postModel = postCollection.items[index];

        if (!postModel) {
            return null;
        }

        const imageAlias = getGridImageAliasForCard(cardSize?.width);

        return (
            <EnchancedPostGridCardCompact
                post={postModel}
                index={index}
                gridColumns={columns}
                isVisible={isVisible}
                imageAlias={imageAlias}
                hasLogPostViewEvent
            />
        );
    };

    return hasPosts ? (
        <Box
            height="100%"
            overflowY="auto"
            ref={gridContainerRef}
            onScroll={onScroll}
            scrollbarGutter="stable"
            dataTestId={DataTestId.SEARCH_POSTS_SCROLL_CONTAINER}
        >
            <ResizableGrid
                key={`${postCollection.items[0]?.id}-search-post-grid`}
                marginTop={2}
                bottomYMargin={8}
                containerRef={gridContainerRef}
                items={postCollection.items}
                loadMore={postCollection.onLoadNext}
                getItemHeight={getItemHeight}
                renderItem={renderItem}
            />
        </Box>
    ) : (
        <SearchNotFound />
    );
};
